import { ChatAdapter } from './ChatAdapter';
import { createConsumer } from '@rails/actioncable';
import { FirebaseChatAdapter } from './FirebaseChatAdapter';

/**
 * GrpcChatAdapter - Implementación del chat usando ActionCable (WebSockets Rails)
 *
 * Este adaptador permite usar ActionCable para comunicación en tiempo real con el servidor
 * Utiliza WebSocket nativo para recibir mensajes en tiempo real y HTTP REST para enviar
 * Base URL: https://api.pibox.app/cable
 *
 * NOTA: Si el historial de gRPC está vacío, intenta leer de Firebase como fallback
 * para conversaciones que iniciaron antes de la migración a gRPC
 */
export class GrpcChatAdapter extends ChatAdapter {
  constructor() {
    super();
    // Base URL para los endpoints de chat (ActionCable)
    this.baseUrl = process.env.REACT_APP_API_URL_BASE || 'https://api.pibox.app';

    // URLs específicas
    this.wsUrl = process.env.REACT_APP_GRPC_URL || 'https://api.picap.app/cable'; // WebSocket URL base
    this.apiUrl = `${this.baseUrl}/api/web/v2/chat`; // HTTP REST URL: https://api.pibox.app/api/web/v2/chat

    // WebSocket consumer
    this.consumer = null;

    // Map<bookingId, subscription>
    this.subscriptions = new Map();

    // Map<bookingId, Array<callbacks>>
    this.messageCallbacks = new Map();

    // Map<bookingId, Set<message_ids>> para evitar duplicados
    this.messageIds = new Map();

    // Map<bookingId, boolean> para saber si ya llegó el history del WebSocket
    this.historyReceived = new Map();

    // Map<bookingId, boolean> para saber si está usando Firebase fallback
    this.usingFirebaseFallback = new Map();

    // Adaptador de Firebase para fallback de conversaciones antiguas
    this.firebaseAdapter = null;

    // Map<bookingId, timeout> para controlar el timeout del indicador "escribiendo"
    this.writingTimeouts = new Map();

    // Map<bookingId, Array<callbacks>> para callbacks de estado de escritura
    this.writingCallbacks = new Map();
  }

  /**
   * Obtiene el token de autenticación para ActionCable
   * Usa directamente el session_token del localStorage
   * @returns {string|null}
   */
  getCableToken = () => {
    return this.getAuthToken();
  }

  /**
   * Inicializa la conexión con el servidor ActionCable
   * Crea el consumer de WebSocket con autenticación
   * @returns {Consumer} Consumer de ActionCable
   */
  initialize = () => {
    if (this.consumer) {
      return this.consumer;
    }

    // Obtener token para autenticación
    const cableToken = this.getCableToken();

    // Conectar con token en query string
    const wsUrl = cableToken ? `${this.wsUrl}?t=${cableToken}` : this.wsUrl;

    this.consumer = createConsumer(wsUrl);

    return this.consumer;
  }

  /**
   * Obtiene el token de autenticación del localStorage
   * @returns {string|null} Token de autenticación
   */
  getAuthToken = () => {
    try {
      // El token se guarda como 'session_token' en localStorage
      const token = localStorage.getItem('session_token');
      return token || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Envía un mensaje a través de ActionCable o Firebase
   * Si está usando Firebase fallback, envía a Firebase
   * Si no, envía a través del WebSocket de gRPC
   * @param {string} message - Contenido del mensaje
   * @param {number} senderType - Tipo de remitente
   * @param {string} bookingId - ID del booking
   */
  sendMessage = async (message, senderType, bookingId) => {
    try {
      // Si está usando Firebase fallback, enviar a Firebase en lugar de gRPC
      if (this.usingFirebaseFallback.get(bookingId)) {
        if (!this.firebaseAdapter) {
          this.firebaseAdapter = new FirebaseChatAdapter();
        }
        return this.firebaseAdapter.sendMessage(message, senderType, bookingId);
      }

      // Enviar por gRPC WebSocket
      const subscription = this.subscriptions.get(bookingId);

      if (subscription && subscription.perform) {
        // Usar el mismo formato que el admin
        const payload = {
          data: {
            uid: Date.now(), // UID único para el mensaje
            content: message,
            file_kind: 'html' // text message
          }
        };

        subscription.perform('create_message', payload);

        // NO hacer optimistic update aquí
        // El mensaje llegará vía WebSocket con el método create_message
        // y se mostrará automáticamente sin duplicados

        return {
          success: true,
          bookingId,
          message
        };
      }

      // No hay suscripción activa - error
      return {
        success: false,
        error: 'No active WebSocket subscription. Please wait for connection.'
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Escucha mensajes nuevos en tiempo real usando ActionCable WebSocket
   * @param {string} bookingId - ID del booking
   * @param {Function} onMessageReceived - Callback cuando llega un nuevo mensaje
   */
  listenForMessages = async (bookingId, onMessageReceived) => {
    try {
      // Verificar si ya existe una suscripción para este booking
      if (this.subscriptions.has(bookingId)) {
        // Agregar el nuevo callback a la lista
        const callbacks = this.messageCallbacks.get(bookingId) || [];
        callbacks.push(onMessageReceived);
        this.messageCallbacks.set(bookingId, callbacks);
        return;
      }

      // Inicializar consumer si no existe
      if (!this.consumer) {
        this.initialize();
      }

      // Crear suscripción al canal BookingChatChannel
      const subscription = this.consumer.subscriptions.create(
        {
          channel: 'BookingChatChannel',
          booking_id: bookingId
        },
        {
          connected: () => {
            // Conectado al canal
          },

          disconnected: () => {
            // Desconectado del canal
          },

          received: (data) => {

            const callbacks = this.messageCallbacks.get(bookingId) || [];

            // Procesar el mensaje según el método
            switch (data.method) {
              case 'history':
                // Historial completo de mensajes al conectarse
                const historyMessages = data.data?.conversation?.messages || [];

                // Marcar que ya recibimos el history para este booking
                this.historyReceived.set(bookingId, true);

                // Si el historial está vacío, intentar leer de Firebase (fallback para conversaciones antiguas)
                if (historyMessages.length === 0) {
                  // Marcar que este booking está usando Firebase fallback
                  this.usingFirebaseFallback.set(bookingId, true);
                  this.loadFirebaseFallback(bookingId, callbacks);
                  break;
                }

                // Invertir el orden para que el más antiguo esté primero y el más reciente al final
                const sortedMessages = [...historyMessages].reverse();

                // Inicializar set de IDs para este booking
                if (!this.messageIds.has(bookingId)) {
                  this.messageIds.set(bookingId, new Set());
                }
                const messageIdSet = this.messageIds.get(bookingId);

                sortedMessages.forEach((msg) => {
                  // Evitar duplicados por ID
                  if (msg.id && messageIdSet.has(msg.id)) {
                    return;
                  }

                  // Mapear user_kind a sender type numérico
                  let senderType;
                  switch (msg.user_kind) {
                    case 'passenger':
                      senderType = this.AS_PASSENGER; // 0
                      break;
                    case 'driver':
                      senderType = this.AS_DRIVER; // 1
                      break;
                    case 'picap':
                      senderType = this.AS_PICAP; // 2
                      break;
                    case 'shopper':
                      senderType = this.AS_SHOPPER; // 3
                      break;
                    default:
                      senderType = msg.is_driver ? this.AS_DRIVER : this.AS_PASSENGER;
                  }

                  const formattedMessage = {
                    id: msg.id || msg._id,  // ✅ Incluir el ID del mensaje
                    message: msg.content || msg.message || msg.text || msg.body,
                    sender: senderType,
                    createdAt: msg.created_at || msg.createdAt || msg.timestamp || Date.now(),
                    // Información de archivo adjunto (audio, imagen, video, etc.)
                    fileUrl: msg.file_url,
                    fileName: msg.file_name,
                    fileKind: msg.file_kind,
                    fileContentType: msg.file_content_type,
                    // Coordenadas de ubicación
                    coordinates: msg.coordinates
                  };

                  // Marcar como procesado
                  if (msg.id) {
                    messageIdSet.add(msg.id);
                  }

                  callbacks.forEach(callback => {
                    try {
                      callback(formattedMessage);
                    } catch (error) {
                      // Error procesando callback
                    }
                  });
                });
                break;

              case 'message':
              case 'new_message':
              case 'create_message':
                // Mensaje nuevo en tiempo real
                const messageData = data.data || data;
                const conversation = messageData.conversation || {};

                // Obtener set de IDs para evitar duplicados
                const newMessageIdSet = this.messageIds.get(bookingId) || new Set();

                // Extraer el mensaje del formato de respuesta
                let formattedMessage = null;

                // Formato: {conversation: {messages: [{...}]}}
                if (conversation.messages && Array.isArray(conversation.messages)) {
                  // El primer mensaje del array es el más reciente (create_message)
                  const newMessage = conversation.messages[0];

                  if (newMessage) {
                    // Verificar si ya procesamos este mensaje
                    if (newMessage.id && newMessageIdSet.has(newMessage.id)) {
                      break;
                    }

                    // Mapear user_kind a sender type numérico
                    let senderType;
                    switch (newMessage.user_kind) {
                      case 'passenger':
                        senderType = this.AS_PASSENGER;
                        break;
                      case 'driver':
                        senderType = this.AS_DRIVER;
                        break;
                      case 'picap':
                        senderType = this.AS_PICAP;
                        break;
                      case 'shopper':
                        senderType = this.AS_SHOPPER;
                        break;
                      default:
                        senderType = newMessage.is_driver ? this.AS_DRIVER : this.AS_PASSENGER;
                    }

                    formattedMessage = {
                      id: newMessage.id || newMessage._id,  // ✅ Incluir el ID del mensaje
                      message: newMessage.content || newMessage.message || newMessage.text,
                      sender: senderType,
                      createdAt: newMessage.created_at || newMessage.createdAt || Date.now(),
                      // Información de archivo adjunto (audio, imagen, video, etc.)
                      fileUrl: newMessage.file_url,
                      fileName: newMessage.file_name,
                      fileKind: newMessage.file_kind,
                      fileContentType: newMessage.file_content_type,
                      // Coordenadas de ubicación
                      coordinates: newMessage.coordinates
                    };

                    // Marcar como procesado
                    if (newMessage.id) {
                      newMessageIdSet.add(newMessage.id);
                    }
                  }
                }

                // Llamar a todos los callbacks registrados
                if (formattedMessage) {
                  callbacks.forEach(callback => {
                    try {
                      callback(formattedMessage);
                    } catch (error) {
                      // Error procesando callback
                    }
                  });
                }
                break;

              case 'writing':
                // Estado de "escribiendo..." del otro usuario
                // El formato real es: data.conversation.writing = { userId: 'Edward is writing' } o {}
                const writingData = data.data || {};
                const conversationData = writingData.conversation || {};
                const writingInfo = conversationData.writing || {};

                // Si writingInfo tiene alguna key, significa que alguien está escribiendo
                const isWriting = Object.keys(writingInfo).length > 0;

                // Obtener el mensaje completo y el userId
                let writingMessage = null;
                let writingUserId = null;
                if (isWriting) {
                  writingUserId = Object.keys(writingInfo)[0];
                  writingMessage = writingInfo[writingUserId]; // Ej: "Edward is writing" - ya viene completo
                }

                // Llamar a todos los callbacks registrados para el estado de escritura
                // Pasamos el mensaje completo y el userId para que el componente pueda filtrar
                const writingCallbacksList = this.writingCallbacks.get(bookingId) || [];
                writingCallbacksList.forEach(callback => {
                  try {
                    callback({
                      isWriting,
                      message: writingMessage,
                      userId: writingUserId
                    });
                  } catch (error) {
                    // Error procesando callback
                  }
                });
                break;

              case 'read':
                // Evento de lectura (read receipt) recibido
                // El servidor notifica que otro usuario ha leído los mensajes
                break;

              case 'badge':
                // Badge/notificación - ignorar por ahora
                break;

              default:
                // Método desconocido
                break;
            }
          },

          rejected: () => {
            // Suscripción rechazada
          }
        }
      );

      // Guardar la suscripción y el callback
      this.subscriptions.set(bookingId, subscription);
      this.messageCallbacks.set(bookingId, [onMessageReceived]);

      // No hay endpoint HTTP funcional, confiamos 100% en el WebSocket
      // El WebSocket enviará el método 'history' con todos los mensajes disponibles

    } catch (error) {
      // Error creando suscripción
    }
  }


  /**
   * Escucha solo el último mensaje usando WebSocket
   * @param {string} bookingId - ID del booking
   * @param {Function} onMessageReceived - Callback cuando llega un nuevo mensaje
   */
  listenLastMessages = (bookingId, onMessageReceived) => {
    // Usa el mismo mecanismo que listenForMessages
    // El WebSocket enviará solo nuevos mensajes automáticamente
    this.listenForMessages(bookingId, onMessageReceived);
  }

  /**
   * Carga mensajes desde Firebase como fallback
   * Se usa cuando el historial de gRPC está vacío (conversaciones antiguas)
   * @param {string} bookingId - ID del booking
   * @param {Array<Function>} callbacks - Array de callbacks para procesar mensajes
   */
  loadFirebaseFallback = async (bookingId, callbacks) => {
    try {
      // Inicializar Firebase adapter si no existe
      if (!this.firebaseAdapter) {
        this.firebaseAdapter = new FirebaseChatAdapter();
      }

      // Inicializar set de IDs para evitar duplicados
      if (!this.messageIds.has(bookingId)) {
        this.messageIds.set(bookingId, new Set());
      }
      const messageIdSet = this.messageIds.get(bookingId);

      // Usar Firebase para cargar mensajes históricos de una sola vez
      const firebaseApp = this.firebaseAdapter.initialize();
      const messagesRef = firebaseApp.database()
        .ref('chat_messages/' + bookingId)
        .orderByKey();

      // Cargar todos los mensajes de una sola vez (once en lugar de on)
      messagesRef.once('value', (snapshot) => {
        const messages = [];
        snapshot.forEach((childSnapshot) => {
          messages.push(childSnapshot.val());
        });

        // Procesar cada mensaje
        messages.forEach((msg) => {
          // Evitar duplicados (aunque no deberían existir en este caso)
          const msgId = `firebase_${msg.createdAt}_${msg.sender}`;
          if (messageIdSet.has(msgId)) {
            return;
          }

          // Los mensajes de Firebase ya vienen en el formato correcto
          callbacks.forEach(callback => {
            try {
              callback(msg);
            } catch (error) {
              // Error procesando callback
            }
          });

          // Marcar como procesado
          messageIdSet.add(msgId);
        });

        // Después de cargar el historial de Firebase, seguir escuchando nuevos mensajes en Firebase
        // (no en gRPC, porque esta conversación está completamente en Firebase)
        messagesRef.on('child_added', (childSnapshot) => {
          const msg = childSnapshot.val();

          // Evitar duplicados
          const msgId = `firebase_${msg.createdAt}_${msg.sender}`;
          if (messageIdSet.has(msgId)) {
            return;
          }

          // Llamar a los callbacks con el nuevo mensaje
          callbacks.forEach(callback => {
            try {
              callback(msg);
            } catch (error) {
              // Error procesando callback
            }
          });

          // Marcar como procesado
          messageIdSet.add(msgId);
        });
      });

    } catch (error) {
      // Error cargando Firebase fallback
    }
  }

  /**
   * Verifica si un booking está usando Firebase fallback
   * @param {string} bookingId - ID del booking
   * @returns {boolean} true si está usando Firebase fallback
   */
  isUsingFirebaseFallback = (bookingId) => {
    return this.usingFirebaseFallback.get(bookingId) || false;
  }

  /**
   * Inicia el indicador de "escribiendo..." para un booking
   * Automáticamente se detiene después de 2 segundos si no se vuelve a llamar
   * @param {string} bookingId - ID del booking
   */
  startWriting = (bookingId) => {
    try {
      const subscription = this.subscriptions.get(bookingId);

      if (!subscription || !subscription.perform) {
        return;
      }

      // Limpiar timeout anterior si existe
      const existingTimeout = this.writingTimeouts.get(bookingId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Enviar acción "writing" con writing: true
      subscription.perform('writing', {
        data: { writing: true }
      });

      // Configurar timeout para detener automáticamente después de 2 segundos
      const timeout = setTimeout(() => {
        this.stopWriting(bookingId);
      }, 2000);

      this.writingTimeouts.set(bookingId, timeout);
    } catch (error) {
      // Error en startWriting
    }
  }

  /**
   * Detiene el indicador de "escribiendo..." para un booking
   * @param {string} bookingId - ID del booking
   */
  stopWriting = (bookingId) => {
    try {
      const subscription = this.subscriptions.get(bookingId);

      if (!subscription || !subscription.perform) {
        return;
      }

      // Limpiar timeout si existe
      const existingTimeout = this.writingTimeouts.get(bookingId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        this.writingTimeouts.delete(bookingId);
      }

      // Enviar acción "writing" con writing: false
      subscription.perform('writing', {
        data: { writing: false }
      });
    } catch (error) {
      // Error en stopWriting
    }
  }

  /**
   * Registra un callback para escuchar cuando el otro usuario está escribiendo
   * @param {string} bookingId - ID del booking
   * @param {Function} onWritingChange - Callback que recibe (isWriting: boolean)
   */
  listenForWriting = (bookingId, onWritingChange) => {
    try {
      // Agregar el callback a la lista
      const callbacks = this.writingCallbacks.get(bookingId) || [];
      callbacks.push(onWritingChange);
      this.writingCallbacks.set(bookingId, callbacks);
    } catch (error) {
      // Error en listenForWriting
    }
  }

  /**
   * Envía un evento de "leído" (read receipt) para un mensaje específico
   * Notifica al servidor que el usuario ha leído el mensaje
   * @param {string} bookingId - ID del booking
   * @param {string} messageId - ID del mensaje a marcar como leído
   */
  sendReadReceipt = (bookingId, messageId) => {
    try {
      if (!messageId) {
        return;
      }

      const subscription = this.subscriptions.get(bookingId);

      if (!subscription || !subscription.perform) {
        return;
      }

      const payload = {
        data: {
          id: messageId
        }
      };

      // Enviar acción "read" con el message_id
      subscription.perform('read', payload);
    } catch (error) {
      // Error en sendReadReceipt
    }
  }

  /**
   * Desconecta los WebSocket subscriptions
   * @param {string} bookingId - ID del booking
   */
  disconnect = (bookingId) => {
    try {
      const subscription = this.subscriptions.get(bookingId);

      if (subscription) {
        subscription.unsubscribe();
        this.subscriptions.delete(bookingId);
      }

      // Limpiar callbacks, IDs de mensajes, flags de history y fallback
      this.messageCallbacks.delete(bookingId);
      this.messageIds.delete(bookingId);
      this.historyReceived.delete(bookingId);
      this.usingFirebaseFallback.delete(bookingId);

      // Limpiar timeout de escritura
      const writingTimeout = this.writingTimeouts.get(bookingId);
      if (writingTimeout) {
        clearTimeout(writingTimeout);
        this.writingTimeouts.delete(bookingId);
      }

      // Limpiar callbacks de escritura
      this.writingCallbacks.delete(bookingId);

    } catch (error) {
      // Error desconectando suscripción
    }
  }

  /**
   * Desconecta todas las suscripciones y cierra el consumer
   */
  disconnectAll = () => {
    try {
      // Desuscribirse de todos los bookings
      this.subscriptions.forEach((subscription, bookingId) => {
        this.disconnect(bookingId);
      });

      // Cerrar el consumer
      if (this.consumer) {
        this.consumer.disconnect();
        this.consumer = null;
      }

      // Limpiar todos los IDs de mensajes, flags de history y fallback
      this.messageIds.clear();
      this.historyReceived.clear();
      this.usingFirebaseFallback.clear();

      // Limpiar todos los timeouts de escritura
      this.writingTimeouts.forEach((timeout) => clearTimeout(timeout));
      this.writingTimeouts.clear();

      // Limpiar todos los callbacks de escritura
      this.writingCallbacks.clear();

    } catch (error) {
      // Error desconectando todo
    }
  }
}

export default GrpcChatAdapter;
